export type InterviewOpeningQuestionType = 'intro' | 'main' | 'followup' | 'candidate_question';
export type InterviewStartupSessionMode = 'new' | 'resume' | null;

export const buildPersonalizedInterviewIntro = (candidateName: string) => {
  const firstName = String(candidateName || '').trim().split(/\s+/).filter(Boolean)[0] || '';
  return `Halo${firstName ? `, ${firstName}` : ''}. Saya TEMI, yang akan memandu sesi interview kamu hari ini.`;
};

export const createPersonalizedIntroPlaybackGuard = () => {
  let started = false;
  return {
    tryStart: () => {
      if (started) return false;
      started = true;
      return true;
    },
  };
};

export const resolveInterviewStartupOpening = ({
  canFinish,
  sessionMode,
  answeredCount,
  currentQuestionPosition,
  candidateName = '',
}: {
  canFinish: boolean;
  sessionMode: InterviewStartupSessionMode;
  answeredCount: number;
  currentQuestionPosition: number;
  candidateName?: string;
}) => {
  const isFreshSession = sessionMode === 'new' && answeredCount === 0;
  const personalizedIntro = isFreshSession
    ? buildPersonalizedInterviewIntro(candidateName)
    : '';

  return {
    phase: canFinish
      ? 'done' as const
      : isFreshSession
      ? 'greeting' as const
      : 'ai_speaking' as const,
    openingSource: canFinish
      ? 'none' as const
      : isFreshSession
      ? 'personalized_intro' as const
      : 'canonical_question' as const,
    standaloneOpeningSegments: personalizedIntro ? [personalizedIntro] : [],
    shouldUseResumeTransition: !canFinish
      && !isFreshSession
      && (sessionMode === 'resume' || answeredCount > 0 || currentQuestionPosition > 1),
  };
};

export const buildTemiQuestionSegments = (
  questionText: string,
  questionIndex: number,
  options: {
    resumeContinuation?: boolean;
    questionType?: InterviewOpeningQuestionType;
  } = {}
) => {
  const safeQuestionText = String(questionText || '').trim();
  if (!safeQuestionText) return [];
  if (options.questionType === 'candidate_question') {
    return [safeQuestionText];
  }
  if (options.resumeContinuation) {
    const resumeLead = questionIndex <= 1
      ? 'Mari kita lanjutkan proses interviewnya.'
      : 'Mari kita lanjutkan proses interviewnya, saya ingin menggali contoh lain dari pengalamanmu.';
    return [resumeLead, safeQuestionText];
  }
  if (questionIndex <= 0) {
    return ['Baik, kita mulai interviewnya.', 'Saya akan menanyakan beberapa hal dari pengalamanmu.', safeQuestionText];
  }
  const transitions = [
    ['Baik, saya lanjut sambil mencatat jawabanmu.', 'Sekarang kita masuk ke bagian berikutnya.'],
    ['Oke, terima kasih.', 'Saya ingin menggali contoh lain dari pengalamanmu.'],
    ['Saya catat poin utamanya.', 'Kita lanjut ke situasi berikutnya.'],
    ['Baik, cukup jelas.', 'Sekarang saya ingin melihat cara kamu mengambil keputusan.'],
  ];
  return [...transitions[(questionIndex - 1) % transitions.length], safeQuestionText];
};
